import React, { useState, useEffect, useRef } from 'react';
import { Chart } from 'react-google-charts';
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer } from 'recharts';
import Spinner from 'react-bootstrap/Spinner';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Table from 'react-bootstrap/Table';
import { DateRangePicker } from 'rsuite';
import axios from 'axios';
import Cookies from 'js-cookie';
import { showToast as toast } from '../toastManager.js';
import errorMessages from '../errorMessages.js';
import '../Opportunites.css';

const { combine, before, afterToday } = DateRangePicker;
const IMPRESSIONS_THRESHOLD = 10;
const KEYWORDS_TABLE_PAGE_SIZE = 10;
const LANDING_PAGES_TABLE_PAGE_SIZE = 10;
const LP_LABELS = {
  ABOVE_AVERAGE: 'Above Average',
  BELOW_AVERAGE: 'Below Average',
  AVERAGE: 'Average',
  nan: 'Not Available',
};

// Format number with commas (e.g. 1,234.5)
const formatNumber = (num, decimals = 1) => {
  const n = Number(num || 0);
  if (Number.isNaN(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// Remove query parameters from URL (keep only base URL)
const cleanUrl = (url) => {
  if (!url) return url;
  const questionMarkIndex = url.indexOf('?');
  return questionMarkIndex > -1 ? url.substring(0, questionMarkIndex) : url;
};

function Opportunities({ ViewedPage, shop_domain, app_url }) {
  ViewedPage(41);

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() - 365);

  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState([]);
  const [activeTab, setActiveTab] = useState('search');
  const [spendByLpData, setSpendByLpData] = useState(null);
  const [keywordsDetails, setKeywordsDetails] = useState([]);
  const [demandGenLandingPages, setDemandGenLandingPages] = useState([]);
  const [funnelCounts, setFunnelCounts] = useState({
    keyword_count: 0,
    ad_group_count: 0,
    landing_pages_over_10_clicks: 0,
  });
  const [topLandingPages, setTopLandingPages] = useState([]);
  const [demandGenData, setDemandGenData] = useState({
    total_spend: 0,
    ad_group_count: 0,
    landing_pages_over_10_clicks: 0,
  });

  // Keywords table: LP filter as checkboxes (All, Below Average, Average)
  const [lpFilterCheckboxes, setLpFilterCheckboxes] = useState({ all: true, below: true, average: true });
  const [keywordsTablePage, setKeywordsTablePage] = useState(1);
  const [keywordsSearch, setKeywordsSearch] = useState('');
  const [keywordsMatchTypeFilter, setKeywordsMatchTypeFilter] = useState(''); // '' = all, else filter by match_type
  const [keywordsSort, setKeywordsSort] = useState({ column: 'spend', direction: 'desc' }); // used for data order only; no sort UI in header
  const [keywordFilterOpen, setKeywordFilterOpen] = useState(false);
  const [lpRelevanceFilterOpen, setLpRelevanceFilterOpen] = useState(false);
  const keywordFilterRef = useRef(null);
  const lpRelevanceFilterRef = useRef(null);
  const pageSpeedPollingIntervalRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (keywordFilterOpen && keywordFilterRef.current && !keywordFilterRef.current.contains(e.target)) {
        setKeywordFilterOpen(false);
      }
      if (lpRelevanceFilterOpen && lpRelevanceFilterRef.current && !lpRelevanceFilterRef.current.contains(e.target)) {
        setLpRelevanceFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [keywordFilterOpen, lpRelevanceFilterOpen]);
  const [landingPagesTablePage, setLandingPagesTablePage] = useState(1);
  /** Page Speed Insights API results for top 5 LPs by spend: [{ url, desktopScore, mobileScore }] */
  const [pageSpeedByUrl, setPageSpeedByUrl] = useState([]);
  /** Loading state for Page Speed Insights API */
  const [pageSpeedLoading, setPageSpeedLoading] = useState(false);
  /** Polling state for Page Speed Insights (track polling attempts and job ID) */
  // polling state exists only for diagnostics; no hard limit on number of polls now
  const [pageSpeedPollingState, setPageSpeedPollingState] = useState({ jobId: null, pollCount: 0 });
  /** Opportunity tab: per-row % Efficiency (one value per table row: Search Below/Average/Above, Demand Gen) */
  const [opportunityEfficiencyByRow, setOpportunityEfficiencyByRow] = useState(['', '', '', '']);
  /** Currency symbol from database (default: ₹) */
  const [currencySymbol, setCurrencySymbol] = useState('₹');
  /** UI: accordion open/close state – all sections minimized by default when opening Opportunities page */
  const [openSections, setOpenSections] = useState({
    spendByLp: true,
    keywords: false,
    funnel: false,
    topLanding: false,
    pageSpeed: false,
    demandGenTargeting: true,
    opportunity: true,
  });
  
  // Format currency with symbol from database and comma-separated number (e.g. ₹1,23,456.78)
  const formatCurrency = (num) => `${currencySymbol}${Number(num || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;

  useEffect(() => {
    if (!Cookies.get('shop')) return;
    const created_at_max = new Date();
    created_at_max.setDate(created_at_max.getDate() - 1);
    const created_at_min = new Date();
    created_at_min.setDate(created_at_min.getDate() - 90);
    setFilterDate([created_at_min, created_at_max]);
  }, []);

  useEffect(() => {
    if (!shop_domain || !filterDate?.length) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [start, end] = filterDate;
    const date_start = formatDate(start);
    const date_end = formatDate(end);
    axios
      .post(app_url + '/api/opportunities/get_opportunities_data', {
        date_start,
        date_end,
      }, { withCredentials: true })
      .then((res) => res.data)
      .then((data) => {
        const d = data?.data || {};
        setSpendByLpData(d.spend_by_lp_relevance || null);
        setKeywordsDetails(d.keywords_details || []);
        setFunnelCounts(d.funnel_counts || {
          keyword_count: 0,
          ad_group_count: 0,
          landing_pages_over_10_clicks: 0,
        });
        setTopLandingPages(d.top_landing_pages || []);
        setDemandGenLandingPages(d.demand_gen?.landing_pages || []);
        setDemandGenData(d.demand_gen || {
          total_spend: 0,
          ad_group_count: 0,
          landing_pages_over_10_clicks: 0,
        });
        // Set currency symbol from API response
        if (d.currency && d.currency.symbol) {
          setCurrencySymbol(d.currency.symbol);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(errorMessages['DATA_LOAD_ERROR']?.title || 'Failed to load data');
        console.error(err);
      });
  }, [shop_domain, app_url, filterDate?.length, filterDate?.[0]?.getTime?.(), filterDate?.[1]?.getTime?.()]);

  // Fetch Page Speed Insights for top 5 landing pages by spend
  useEffect(() => {
    // polling version: start job and then query status until complete or max polls
    if (!app_url || !topLandingPages?.length) {
      setPageSpeedByUrl([]);
      setPageSpeedLoading(false);
      return;
    }
    const top5 = [...topLandingPages]
      .sort((a, b) => (Number(b.cost) || 0) - (Number(a.cost) || 0))
      .slice(0, 5);
    const urls = top5.map((r) => cleanUrl(r.landing_page_url)).filter(Boolean);
    if (urls.length === 0) {
      setPageSpeedByUrl([]);
      setPageSpeedLoading(false);
      return;
    }

    let cancelled = false;
    let pollCount = 0;

    setPageSpeedLoading(true);
    // clear any leftover interval
    if (pageSpeedPollingIntervalRef.current) {
      clearInterval(pageSpeedPollingIntervalRef.current);
      pageSpeedPollingIntervalRef.current = null;
    }

    axios
      .post(app_url + '/api/google/start_page_speed_job', { urls }, { withCredentials: true })
      .then((res) => {
        const jobId = res.data?.jobId;
        if (jobId) {
          setPageSpeedPollingState({ jobId, pollCount: 0 });
          pageSpeedPollingIntervalRef.current = setInterval(async () => {
            pollCount += 1;
            setPageSpeedPollingState((prev) => ({ ...prev, pollCount }));
            try {
              // cache‑busting query and explicit no-cache headers
              const statusRes = await axios.get(
                `${app_url}/api/google/page_speed_job/${jobId}?_=${Date.now()}`,
                {
                  withCredentials: true,
                  headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0',
                  },
                }
              );
              const job = statusRes.data?.job;
              if (job) {
                if (job.status === 'running') {
                  // still working; keep polling until status changes
                  return;
                }

                // anything other than running means finished
                if (!cancelled) setPageSpeedByUrl(job.result?.data || []);
                setPageSpeedLoading(false);
                clearInterval(pageSpeedPollingIntervalRef.current);
                pageSpeedPollingIntervalRef.current = null;

                if (job.status === 'failed') {
                  console.error('Page speed job failed', job.error);
                }
              }
            } catch (err) {
              console.error('Polling error for page speed job:', err);
              // continue polling in case of transient network errors
            }
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to start page speed job:', err);
        setPageSpeedLoading(false);
      });

    return () => {
      cancelled = true;
      if (pageSpeedPollingIntervalRef.current) {
        clearInterval(pageSpeedPollingIntervalRef.current);
        pageSpeedPollingIntervalRef.current = null;
      }
    };
  }, [app_url, topLandingPages]);

  // Normalize LP relevance for display
  const normalizeLpRelevance = (v) => {
    if (v === null || v === undefined) return 'nan';
    const s = String(v).trim().toUpperCase();
    if (s === 'NAN' || s === '') return 'nan';
    // Backend already returns normalized values, but keep normalization for edge cases
    if (s.includes('ABOVE') && s.includes('AVERAGE')) return 'ABOVE_AVERAGE';
    if (s.includes('BELOW') && s.includes('AVERAGE')) return 'BELOW_AVERAGE';
    if (s.includes('AVERAGE') && !s.includes('ABOVE') && !s.includes('BELOW')) return 'AVERAGE';
    return 'nan';
  };

  // Normalize speed_score for display: null, undefined, or "nan" (case-insensitive) -> "--"; else show value
  const formatSpeedScore = (v) => {
    if (v == null || v === undefined) return '--';
    const s = String(v).trim().toLowerCase();
    if (s === 'nan' || s === '') return '--';
    return v;
  };

  // Format funnel values: numbers >= 1000 as "1.35K", else as-is
  const formatFunnelValue = (val) => {
    if (val === undefined || val === null) return '0';
    const n = Number(val);
    if (Number.isNaN(n)) return '0';
    if (n >= 1000) {
      const k = (n / 1000).toFixed(2).replace(/\.?0+$/, '');
      return `${k}K`;
    }
    return String(n);
  };

  // Pie: Spend by LP Experience from API
  const pieData = (() => {
    if (!spendByLpData) {
      return { chartRows: [['LP Experience', 'Spend']], totalCost: 0, subOptimalPct: 0, subOptimalCount: 0 };
    }
    const byLp = spendByLpData.spend_by_lp_relevance || [];
    const rows = [['LP Experience', 'Spend', { type: 'string', role: 'tooltip', p: { html: true } }]];
    let totalCost = 0;
    let subOptimalCost = 0;
    let subOptimalCount = 0;
    byLp.forEach((bucket) => {
      const label = LP_LABELS[bucket.lp_relevance] || bucket.lp_relevance;
      const cost = bucket.spend || 0;
      const count = bucket.keyword_count || 0;
      totalCost += cost;
      if (bucket.lp_relevance === 'BELOW_AVERAGE' || bucket.lp_relevance === 'AVERAGE') {
        subOptimalCost += cost;
        subOptimalCount += count;
      }
      rows.push([
        label,
        cost,
        `<div style="padding:8px;">${label}<br/>Spend: ${formatCurrency(cost)}<br/>Keywords: ${formatNumber(count, 0)}</div>`,
      ]);
    });
    const subOptimalPct = totalCost > 0 ? (subOptimalCost / totalCost) * 100 : 0;
    return { chartRows: rows, totalCost, subOptimalPct, subOptimalCount };
  })();

  // Keywords table: filter API keywords_details by LP checkboxes, then apply sorting
  const keywordsTableRows = (() => {
    let list = keywordsDetails;
    const { all, below, average } = lpFilterCheckboxes;
    if (!all && !below && !average) {
      list = [];
    } else if (!all) {
      list = list.filter((r) => {
        const norm = normalizeLpRelevance(r.post_click_quality_score);
        return (below && norm === 'BELOW_AVERAGE') || (average && norm === 'AVERAGE');
      });
    }

    const dir = keywordsSort.direction === 'asc' ? 1 : -1;

    const getLpRank = (row) => {
      const norm = normalizeLpRelevance(row.post_click_quality_score);
      if (norm === 'ABOVE_AVERAGE') return 3;
      if (norm === 'AVERAGE') return 2;
      if (norm === 'BELOW_AVERAGE') return 1;
      return 0;
    };

    const sorted = [...list].sort((a, b) => {
      switch (keywordsSort.column) {
        case 'keyword': {
          const ak = (a.keyword_text || '').toLowerCase();
          const bk = (b.keyword_text || '').toLowerCase();
          if (ak === bk) return 0;
          return ak < bk ? -1 * dir : 1 * dir;
        }
        case 'campaign_id': {
          const ac = (a.campaign_id || '').toString().toLowerCase();
          const bc = (b.campaign_id || '').toString().toLowerCase();
          if (ac === bc) return 0;
          return ac < bc ? -1 * dir : 1 * dir;
        }
        case 'spend': {
          const av = Number(a.cost) || 0;
          const bv = Number(b.cost) || 0;
          return dir === 1 ? av - bv : bv - av;
        }
        case 'conversions': {
          const av = Number(a.conversions) || 0;
          const bv = Number(b.conversions) || 0;
          return dir === 1 ? av - bv : bv - av;
        }
        case 'quality_score': {
          const av = Number(a.quality_score) || 0;
          const bv = Number(b.quality_score) || 0;
          return dir === 1 ? av - bv : bv - av;
        }
        case 'lp_relevance': {
          const av = getLpRank(a);
          const bv = getLpRank(b);
          return dir === 1 ? av - bv : bv - av;
        }
        case 'campaign_name': {
          const acn = (a.campaign_name || '').toLowerCase();
          const bcn = (b.campaign_name || '').toLowerCase();
          if (acn === bcn) return 0;
          return acn < bcn ? -1 * dir : 1 * dir;
        }
        case 'match_type': {
          const am = (a.match_type || '').toLowerCase();
          const bm = (b.match_type || '').toLowerCase();
          if (am === bm) return 0;
          return am < bm ? -1 * dir : 1 * dir;
        }
        default: {
          // Default to spend desc if unknown
          const av = Number(a.cost) || 0;
          const bv = Number(b.cost) || 0;
          return bv - av;
        }
      }
    });

    return sorted;
  })();

  // Keywords table: column filters (keyword text, match type) and pagination (client-side)
  const keywordsSearchLower = (keywordsSearch || '').trim().toLowerCase();
  const keywordsFilteredRows = (() => {
    let rows = keywordsTableRows;
    if (keywordsSearchLower) {
      rows = rows.filter((r) => (r.keyword_text || '').toLowerCase().includes(keywordsSearchLower));
    }
    if (keywordsMatchTypeFilter) {
      rows = rows.filter((r) => (r.match_type || '').trim() === keywordsMatchTypeFilter);
    }
    return rows;
  })();
  // Distinct match types for filter dropdown (from current data)
  const keywordsMatchTypeOptions = (() => {
    const set = new Set();
    keywordsDetails.forEach((r) => {
      const m = (r.match_type || '').trim();
      if (m) set.add(m);
    });
    return ['', ...Array.from(set).sort()];
  })();
  const keywordsTotalRows = keywordsFilteredRows.length;
  const keywordsTotalPages = Math.max(1, Math.ceil(keywordsTotalRows / KEYWORDS_TABLE_PAGE_SIZE));
  const keywordsEffectivePage = Math.max(1, Math.min(keywordsTablePage, keywordsTotalPages));
  const paginatedKeywordsRows = keywordsFilteredRows.slice(
    (keywordsEffectivePage - 1) * KEYWORDS_TABLE_PAGE_SIZE,
    keywordsEffectivePage * KEYWORDS_TABLE_PAGE_SIZE
  );

  // Landing pages tables: only include pages with ≥10 clicks, deduplicate by cleaned URL and then paginate
  const topLandingPagesOver10 = topLandingPages.filter((p) => Number(p.clicks) >= 10);
  const deduplicatedTopLandingPages = (() => {
    const seen = new Set();
    const result = [];
    topLandingPagesOver10.forEach((page) => {
      const cleanedUrl = cleanUrl(page.landing_page_url);
      if (!seen.has(cleanedUrl)) {
        seen.add(cleanedUrl);
        result.push(page);
      }
    });
    return result;
  })();
  const landingPagesTotalRows = deduplicatedTopLandingPages.length;
  const landingPagesTotalPages = Math.max(1, Math.ceil(landingPagesTotalRows / LANDING_PAGES_TABLE_PAGE_SIZE));
  const landingPagesEffectivePage = Math.max(1, Math.min(landingPagesTablePage, landingPagesTotalPages));
  const paginatedTopLandingPages = deduplicatedTopLandingPages.slice(
    (landingPagesEffectivePage - 1) * LANDING_PAGES_TABLE_PAGE_SIZE,
    landingPagesEffectivePage * LANDING_PAGES_TABLE_PAGE_SIZE
  );
  // Top 5 landing pages by spend (for Page Speed by Landing Pages; data from Page Speed Insights API)
  const top5LandingPagesBySpend = [...deduplicatedTopLandingPages]
    .sort((a, b) => (Number(b.cost) || 0) - (Number(a.cost) || 0))
    .slice(0, 5);
  const getPageSpeedForUrl = (url) => pageSpeedByUrl.find((e) => e.url === cleanUrl(url)) || null;

  // Funnel: from API (no impression > 10 filter for keywords/ad groups)
  const funnelDisplay = {
    keywords: funnelCounts.keyword_count || 0,
    adGroups: funnelCounts.ad_group_count || 0,
    landingPagesOver10Clicks: funnelCounts.landing_pages_over_10_clicks || 0,
  };
  // Search tab: funnel chart data. Values should reflect LP ≥10 clicks, now derived from the filtered list
  const searchLpOver10 = deduplicatedTopLandingPages.length;
  const searchFunnelData = [
    { name: 'Keywords', value: 3, realValue: funnelDisplay.keywords, fill: '#4a6fa5' },
    { name: 'Ad groups', value: 2, realValue: funnelDisplay.adGroups, fill: '#6b8fc4' },
    { name: 'LP', value: 1, realValue: searchLpOver10, fill: '#7398d4' },
  ];
  // Demand Gen tab: funnel chart data for Targeting & Landing Pages (Non-Search campaigns).
  //const demandGenAdGroupCount = demandGenData.ad_group_count || 0;
  //const demandGenLpOver10 = demandGenData.landing_pages_over_10_clicks || 0;

  const distinctDemandGenLpCount = (() => {
  const baseUrls = new Set();
  //already filtered by clicks > 10 in backend, so no need to filter again here 
  //demandGenLandingPages.forEach(lp => {
  //   if (lp.clicks > 10) {
  //     const cleaned = cleanUrl(lp.landing_page_url);
  //     if (cleaned) baseUrls.add(cleaned);
  //   }
  // });
  
  demandGenLandingPages.forEach(lp => {
    const cleaned = cleanUrl(lp.landing_page_url);
    if (cleaned) baseUrls.add(cleaned);
  });
  return baseUrls.size || demandGenData.landing_pages_over_10_clicks || 0;
})();


const demandGenFunnelData = [
  { name: 'Ad groups', realValue: demandGenData.ad_group_count || 0, fill: '#576FA2' },
  { name: 'LP', realValue: distinctDemandGenLpCount, fill: '#768EC1' },
];
  // const demandGenFunnelData = [
  //   { name: 'Ad groups', realValue: demandGenAdGroupCount, fill: '#576FA2' },
  //   { name: 'LP', realValue: demandGenLpOver10, fill: '#768EC1' },
  // ];

  // Opportunity tab: spend by channel/category from Search (LP relevance) and Demand Gen
  const opportunitySpendByLp = (() => {
    const byLp = {};
    (spendByLpData?.spend_by_lp_relevance || []).forEach((b) => {
      byLp[b.lp_relevance] = Number(b.spend) || 0;
    });
    return {
      belowAverage: byLp.BELOW_AVERAGE ?? 0,
      average: byLp.AVERAGE ?? 0,
      aboveAverage: byLp.ABOVE_AVERAGE ?? 0,
    };
  })();
  const demandGenSpend = Number(demandGenData.total_spend) || 0;
  const opportunityRows = [
    { channel: 'Search', category: 'Below average LP', currentSpend: opportunitySpendByLp.belowAverage },
    { channel: 'Search', category: 'Average LP', currentSpend: opportunitySpendByLp.average },
    { channel: 'Search', category: 'Above average LP', currentSpend: opportunitySpendByLp.aboveAverage },
    { channel: 'Demand Gen & Others', category: '', currentSpend: demandGenSpend },
  ];
  const opportunityTotalSpend = opportunityRows.reduce((s, r) => s + r.currentSpend, 0);
  const opportunityRowsWithSavings = opportunityRows.map((r, i) => {
    const efficiencyNum = Math.min(100, Math.max(0, Number(opportunityEfficiencyByRow[i]) || 0));
    return {
      ...r,
      efficiencyPct: opportunityEfficiencyByRow[i],
      savings: (r.currentSpend * efficiencyNum) / 100,
    };
  });
  const opportunityTotalSavings = opportunityRowsWithSavings.reduce((s, r) => s + r.savings, 0);
  const setOpportunityEfficiencyForRow = (rowIndex, value) => {
    setOpportunityEfficiencyByRow((prev) => {
      const next = [...prev];
      next[rowIndex] = value;
      return next;
    });
  };

  function formatDate(date, isIncreaseDate = false) {
    if (!date) return '';
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + (isIncreaseDate ? d.getDate() + 1 : d.getDate());
    const year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  }

  const handleDateChange = (dateRange) => {
    if (dateRange && dateRange.length === 2) setFilterDate(dateRange);
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!shop_domain) {
    return (
      <div className="main-container">
        <div className="go_to_shopify_admin_div">
          <h5 style={{ display: 'inline-block' }}>!! Go to Admin Dashboard and open our app !!</h5>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="opportunities-header">
        <div className="opportunities-header-inner">
          <div className="main-title p-0">
            <h2>Landing Page Audit</h2>
          </div>
          {/* <p className="admin-subtitle mb-0">Manage and track your sales pipeline</p> */}
          <div className="opportunities-tabs-and-filter">
            <div className="opportunities-tabs-wrapper">
              <Nav className="opportunities-tabs">
                <Nav.Item>
                  <Nav.Link
                    className={activeTab === 'search' ? 'active' : ''}
                    onClick={() => setActiveTab('search')}
                  >
                    Search
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    className={activeTab === 'demand-gen' ? 'active' : ''}
                    onClick={() => setActiveTab('demand-gen')}
                  >
                    Demand Gen & Others
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    className={activeTab === 'opportunity' ? 'active' : ''}
                    onClick={() => setActiveTab('opportunity')}
                  >
                    Opportunities
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
            <div className="opportunities-date-card">
              <DateRangePicker
                shouldDisableDate={combine(before(maxDate), afterToday())}
                editable={false}
                value={filterDate}
                format="yyyy-MM-dd"
                character=" - "
                showOneCalendar
                block
                onChange={handleDateChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Container fluid className="p-0 mt-3">
        {loading ? (
          <div className="admin-content">
            <div className="main_conatiner_spinner">
              <Spinner animation="border" />
            </div>
          </div>
        ) : (
          <div className="admin-content">
            {/* ---- Search tab ---- */}
            <div className={`admin-tab-content mt-4 ${activeTab === 'search' ? 'active' : 'hidden'}`}>
              {/* Graph 1: Spend by LP Experience - Pie */}
              <div className="opportunities-section mb-4">
                <button
                  type="button"
                  className="opportunities-section-header"
                  onClick={() => toggleSection('spendByLp')}
                  aria-expanded={openSections.spendByLp}
                >
                  <div>
                    <h5 className="mb-1">Spend By Landing Page Relevance</h5>
                    {pieData.totalCost > 0 && (
                      <p className="opportunities-section-subtitle mb-0">
                        <strong>{Number(pieData.subOptimalPct ?? 0).toFixed(1)}%</strong> Spend  is going to keywords with sub-optimal experience (Below Average or Average)
                      </p>
                    )}
                  </div>
                  <span className={`opportunities-section-chevron ${openSections.spendByLp ? 'open' : ''}`} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                {openSections.spendByLp && (
                  <div className="opportunities-section-body">
                    <div className="opportunities-pie-chart-card">
                      {pieData.chartRows.length > 1 ? (
                        <>
                          <div className="opportunities-pie-top">
                            <span />
                            <div className="opportunities-pie-total">
                              <div className="opportunities-pie-total-amount">{formatCurrency(pieData.totalCost)}</div>
                              <div className="opportunities-pie-total-label">TOTAL</div>
                            </div>
                          </div>
                          <div className="opportunities-pie-chart-wrap">
                            <Chart
                              chartType="PieChart"
                              width="100%"
                              height="400px"
                              data={pieData.chartRows}
                              options={{
                                title: '',
                                pieHole: 0.4,
                                tooltip: { isHtml: true },
                                legend: { position: 'bottom' },
                                chartArea: { width: '85%', height: '75%' },
                                slices: {
                                  0: { color: '#98B9FF' },
                                  1: { color: '#F69292' },
                                  2: { color: '#FFDF87' },
                                  3: { color: '#FFAF4E' },
                                },
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <p className="text-muted mb-0">No data (need keywords with impressions &gt; 10).</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Graph 2: Keyword Details - Table with sub-tabs, pagination */}
              <div className="opportunities-section mb-4">
                <button
                  type="button"
                  className="opportunities-section-header"
                  onClick={() => toggleSection('keywords')}
                  aria-expanded={openSections.keywords}
                >
                  <div>
                    <h5 className="mb-1">Keyword Details</h5>
                    {/* <p className="opportunities-section-subtitle mb-0">
                      Define audience segments and content strategy.
                    </p> */}
                  </div>
                  <span className={`opportunities-section-chevron ${openSections.keywords ? 'open' : ''}`} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                {openSections.keywords && (
                  <div className="opportunities-section-body">
                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3 opp-padding-x">
                      <Nav variant="pills" className="opportunities-keywords-nav">
                        <Nav.Item>
                          <Nav.Link
                            active={lpFilterCheckboxes.all}
                            onClick={() => {
                              setLpFilterCheckboxes({ all: true, below: true, average: true });
                              setKeywordsTablePage(1);
                            }}
                          >
                            All
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link
                            active={!lpFilterCheckboxes.all && lpFilterCheckboxes.below && !lpFilterCheckboxes.average}
                            onClick={() => {
                              setLpFilterCheckboxes({ all: false, below: true, average: false });
                              setKeywordsTablePage(1);
                            }}
                          >
                            Below Average
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link
                            active={!lpFilterCheckboxes.all && !lpFilterCheckboxes.below && lpFilterCheckboxes.average}
                            onClick={() => {
                              setLpFilterCheckboxes({ all: false, below: false, average: true });
                              setKeywordsTablePage(1);
                            }}
                          >
                            Average
                          </Nav.Link>
                        </Nav.Item>
                      </Nav>
                    </div>
                    <div className="opportunities-keywords-table opportunities-keywords-table-dropdowns border rounded bg-white opp-padding">
                      <Table responsive className="mb-0">
                    <thead>
                      <tr>
                        <th className="channel_performance_header_cell opportunities-keyword-header-cell">
                          <div ref={keywordFilterRef} className="opportunities-keyword-header-content opportunities-header-dropdown-wrapper">
                            <button
                              type="button"
                              className="text-decoration-none opportunities-keyword-header-toggle"
                              onClick={() => setKeywordFilterOpen((o) => !o)}
                              aria-expanded={keywordFilterOpen}
                              aria-label={keywordFilterOpen ? 'Close keyword search' : 'Open keyword search'}
                            >
                              Keyword
                              <span className={`opportunities-header-chevron ${keywordFilterOpen ? 'open' : ''}`} aria-hidden="true">▼</span>
                            </button>
                            {keywordFilterOpen && (
                              <div className="opportunities-keyword-dropdown" onClick={(e) => e.stopPropagation()}>
                                <div className="opportunities-keywords-search-wrapper">
                                  <span className="opportunities-keywords-search-icon" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M9.58341 15.8337C12.7951 15.8337 15.4167 13.2121 15.4167 10.0003C15.4167 6.78862 12.7951 4.16699 9.58341 4.16699C6.37175 4.16699 3.75008 6.78862 3.75008 10.0003C3.75008 13.2121 6.37175 15.8337 9.58341 15.8337Z" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M16.25 16.667L14.5833 15.0003" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control-search-keywords opportunities-keywords-search-input"
                                    placeholder="Enter Keyword"
                                    value={keywordsSearch}
                                    onChange={(e) => {
                                      setKeywordsSearch(e.target.value);
                                      setKeywordsTablePage(1);
                                    }}
                                    aria-label="Search keyword"
                                  />
                                </div>
                                <button
                                  type="button"
                                  className="opportunities-keywords-search-apply"
                                  onClick={() => setKeywordsTablePage(1)}
                                >
                                  Apply
                                </button>
                              </div>
                            )}
                          </div>
                        </th>
                        <th className="channel_performance_header_cell">Spend</th>
                        <th className="channel_performance_header_cell">Conversions</th>
                        <th className="channel_performance_header_cell">Quality Score</th>
                        <th className="channel_performance_header_cell opportunities-lp-relevance-header-cell">
                          <div ref={lpRelevanceFilterRef} className="opportunities-lp-relevance-header-content opportunities-header-dropdown-wrapper">
                            <button
                              type="button"
                              className="p-0 text-decoration-none opportunities-lp-relevance-header-toggle"
                              onClick={() => setLpRelevanceFilterOpen((o) => !o)}
                              aria-expanded={lpRelevanceFilterOpen}
                              aria-label={lpRelevanceFilterOpen ? 'Close landing page relevance filter' : 'Open landing page relevance filter'}
                            >
                              Landing Page Relevance
                              <span className={`opportunities-header-chevron ${lpRelevanceFilterOpen ? 'open' : ''}`} aria-hidden="true">▼</span>
                            </button>
                            {lpRelevanceFilterOpen && (
                              <div className="opportunities-lp-relevance-dropdown" onClick={(e) => e.stopPropagation()}>
                                <label className="opportunities-keywords-filter-option">
                                  <input
                                    type="checkbox"
                                    checked={lpFilterCheckboxes.all}
                                    onChange={() => {
                                      setLpFilterCheckboxes((prev) => {
                                        const nextAll = !prev.all;
                                        return { all: nextAll, below: nextAll ? true : prev.below, average: nextAll ? true : prev.average };
                                      });
                                      setKeywordsTablePage(1);
                                    }}
                                  />
                                  <span>All</span>
                                </label>
                                <label className="opportunities-keywords-filter-option">
                                  <input
                                    type="checkbox"
                                    checked={lpFilterCheckboxes.below}
                                    onChange={() => {
                                      setLpFilterCheckboxes((prev) => ({ ...prev, below: !prev.below, all: false }));
                                      setKeywordsTablePage(1);
                                    }}
                                  />
                                  <span>Below Average</span>
                                </label>
                                <label className="opportunities-keywords-filter-option">
                                  <input
                                    type="checkbox"
                                    checked={lpFilterCheckboxes.average}
                                    onChange={() => {
                                      setLpFilterCheckboxes((prev) => ({ ...prev, average: !prev.average, all: false }));
                                      setKeywordsTablePage(1);
                                    }}
                                  />
                                  <span>Average</span>
                                </label>
                              </div>
                            )}
                          </div>
                        </th>
                        <th className="channel_performance_header_cell">Match Type</th>
                        <th className="channel_performance_header_cell">Campaign Name</th>
                        <th className="channel_performance_header_cell">Campaign ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedKeywordsRows.map((r, i) => (
                        <tr
                          key={(keywordsEffectivePage - 1) * KEYWORDS_TABLE_PAGE_SIZE + i}
                          className={i % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}
                        >
                          <td className="channel_performance_table_cell">{r.keyword_text || '--'}</td>
                          <td className="channel_performance_table_cell">{formatCurrency(r.cost)}</td>
                          <td className="channel_performance_table_cell">{formatNumber(r.conversions)}</td>
                          <td className="channel_performance_table_cell">{r.quality_score ?? '--'}</td>
                          <td className="channel_performance_table_cell">{LP_LABELS[normalizeLpRelevance(r.post_click_quality_score)] || r.post_click_quality_score || 'nan'}</td>
                          <td className="channel_performance_table_cell">{r.match_type || '--'}</td>
                          <td className="channel_performance_table_cell">{r.campaign_name || '--'}</td>
                          <td className="channel_performance_table_cell">{r.campaign_id || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {keywordsTotalRows > 0 && (
                    <div className="opportunities-table-pagination">
                      <button
                        type="button"
                        className="opportunities-page-nav opportunities-page-nav-prev"
                        onClick={() => setKeywordsTablePage((p) => Math.max(1, p - 1))}
                        disabled={keywordsEffectivePage <= 1}
                        aria-label="Previous page"
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.8334 10.0003H4.16675M4.16675 10.0003L10.0001 15.8337M4.16675 10.0003L10.0001 4.16699" stroke="#344054" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {/* <span>Previous</span> */}
                      </button>
                      <nav className="d-flex align-items-center gap-1 opportunities-page-numbers" aria-label="Keywords table pagination">
                        {(() => {
                          const items = [];
                          const total = keywordsTotalPages;
                          const current = keywordsEffectivePage;
                          if (total <= 7) {
                            for (let p = 1; p <= total; p++) items.push(p);
                          } else {
                            items.push(1);
                            if (current > 3) items.push('...');
                            for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
                              if (!items.includes(p)) items.push(p);
                            }
                            if (current < total - 2) items.push('...');
                            if (total > 1) items.push(total);
                          }
                          return items.map((p, idx) =>
                            p === '...' ? (
                              <span key={`ellipsis-${idx}`} className="px-2 text-muted">…</span>
                            ) : (
                              <button
                                key={p}
                                type="button"
                                className={`btn btn-sm rounded ${current === p ? 'sxi_primary_lightblue_btn' : 'opportunities-pagination-outline'}`}
                                onClick={() => setKeywordsTablePage(p)}
                                aria-label={`Page ${p}`}
                                aria-current={current === p ? 'page' : undefined}
                              >
                                {p}
                              </button>
                            )
                          );
                        })()}
                      </nav>
                      <button
                        type="button"
                        className="opportunities-page-nav opportunities-page-nav-next"
                        onClick={() => setKeywordsTablePage((p) => Math.min(keywordsTotalPages, p + 1))}
                        disabled={keywordsEffectivePage >= keywordsTotalPages}
                        aria-label="Next page"
                      >
                        {/* <span>Next</span> */}
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.16675 10.0003H15.8334M15.8334 10.0003L10.0001 4.16699M15.8334 10.0003L10.0001 15.8337" stroke="#344054" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                    </div>
                    {keywordsTableRows.length === 0 && (
                      <p className="text-muted mt-2 mb-0">
                        No keywords with LP Relevance Below Average or Average and impressions &gt; 10.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="opportunities-section mb-4">
                <button
                  type="button"
                  className="opportunities-section-header"
                  onClick={() => toggleSection('funnel')}
                  aria-expanded={openSections.funnel}
                >
                  <div>
                    <h5 className="mb-1">Targeting - Landing Pages Funnel</h5>
                    {/* <p className="opportunities-section-subtitle mb-0">
                      Distribution of keywords from total targeting to active landing pages receiving meaningful traffic (&gt;10 clicks).
                    </p> */}
                  </div>
                  <span className={`opportunities-section-chevron ${openSections.funnel ? 'open' : ''}`} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                {openSections.funnel && (
                  <div className="opportunities-section-body">
                    <div className="border rounded overflow-hidden bg-white" style={{ padding: '16px', maxWidth: 520 }}>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={searchFunnelData} margin={{ top: 20, right: 16, left: 16, bottom: 24 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatFunnelValue(v)} />
                          <Bar dataKey="realValue" radius={[4, 4, 0, 0]} label={{ position: 'top', formatter: (v) => formatFunnelValue(v), fontSize: 12 }}>
                            {searchFunnelData.map((entry) => (
                              <Cell key={entry.name} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>

              {/* Graph 4: Top Landing Pages */}
              <div className="opportunities-section mb-4">
                <button
                  type="button"
                  className="opportunities-section-header"
                  onClick={() => toggleSection('topLanding')}
                  aria-expanded={openSections.topLanding}
                >
                  <div>
                    <h5 className="mb-1">Top Landing Pages</h5>
                    {/* <p className="opportunities-section-subtitle mb-0">
                      Define audience segments and content strategy.
                    </p> */}
                  </div>
                  <span className={`opportunities-section-chevron ${openSections.topLanding ? 'open' : ''}`} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                {openSections.topLanding && (
                  <div className="opportunities-section-body">
                    {topLandingPages.length === 0 ? (
                      <p className="text-muted mb-0">No landing pages with &gt; 10 clicks in this period.</p>
                    ) : (
                      <>
                        <div className="opportunities-keywords-table border rounded overflow-hidden bg-white opp-padding">
                          <Table responsive className="mb-0">
                            <thead>
                              <tr>
                                <th className="channel_performance_header_cell">Landing Page URL</th>
                                <th className="channel_performance_header_cell">Spend</th>
                                <th className="channel_performance_header_cell">Clicks</th>
                                <th className="channel_performance_header_cell">Conversions</th>
                                <th className="channel_performance_header_cell">Speed Score</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedTopLandingPages.map((row, i) => (
                                <tr
                                  key={(landingPagesEffectivePage - 1) * LANDING_PAGES_TABLE_PAGE_SIZE + i}
                                  className={i % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}
                                >
                                  <td className="channel_performance_table_cell">{cleanUrl(row.landing_page_url) || '--'}</td>
                                  <td className="channel_performance_table_cell">{formatCurrency(row.cost)}</td>
                                  <td className="channel_performance_table_cell">{formatNumber(row.clicks)}</td>
                                  <td className="channel_performance_table_cell">{formatNumber(row.conversions)}</td>
                                  <td className="channel_performance_table_cell">{(() => {
                                        // prefer fresh data from pageSpeedByUrl if available
                                        const ps = getPageSpeedForUrl(row.landing_page_url);
                                        if (ps) {
                                          if (ps.desktopError || ps.mobileError) return 'Err';
                                          const score = ps.mobileScore != null ? ps.mobileScore : ps.desktopScore;
                                          return score != null ? Number(score).toFixed(1) : '--';
                                        }
                                        // fallback to whatever the server originally returned (older cache)
                                        const v = formatSpeedScore(row.speed_score);
                                        return v === '--' ? '--' : Number(v).toFixed(1);
                                      })()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {landingPagesTotalRows > 0 && (
                            <div className="opportunities-table-pagination">
                              <button
                                type="button"
                                className="opportunities-page-nav opportunities-page-nav-prev"
                                onClick={() => setLandingPagesTablePage((p) => Math.max(1, p - 1))}
                                disabled={landingPagesEffectivePage <= 1}
                                aria-label="Previous page"
                              >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M15.8334 10.0003H4.16675M4.16675 10.0003L10.0001 15.8337M4.16675 10.0003L10.0001 4.16699" stroke="#344054" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {/* <span>Previous</span> */}
                              </button>
                              <nav className="d-flex align-items-center gap-1 opportunities-page-numbers" aria-label="Top Landing Pages pagination">
                                {(() => {
                                  const items = [];
                                  const total = landingPagesTotalPages;
                                  const current = landingPagesEffectivePage;
                                  if (total <= 7) {
                                    for (let p = 1; p <= total; p++) items.push(p);
                                  } else {
                                    items.push(1);
                                    if (current > 3) items.push('...');
                                    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
                                      if (!items.includes(p)) items.push(p);
                                    }
                                    if (current < total - 2) items.push('...');
                                    if (total > 1) items.push(total);
                                  }
                                  return items.map((p, idx) =>
                                    p === '...' ? (
                                      <span key={`lp-ellipsis-${idx}`} className="px-2 text-muted">…</span>
                                    ) : (
                                      <button
                                        key={p}
                                        type="button"
                                        className={`btn btn-sm rounded ${current === p ? 'sxi_primary_lightblue_btn' : 'opportunities-pagination-outline'}`}
                                        onClick={() => setLandingPagesTablePage(p)}
                                        aria-label={`Page ${p}`}
                                        aria-current={current === p ? 'page' : undefined}
                                      >
                                        {p}
                                      </button>
                                    )
                                  );
                                })()}
                              </nav>
                              <button
                                type="button"
                                className="opportunities-page-nav opportunities-page-nav-next"
                                onClick={() => setLandingPagesTablePage((p) => Math.min(landingPagesTotalPages, p + 1))}
                                disabled={landingPagesEffectivePage >= landingPagesTotalPages}
                                aria-label="Next page"
                              >
                                {/* <span>Next</span> */}
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M4.16675 10.0003H15.8334M15.8334 10.0003L10.0001 4.16699M15.8334 10.0003L10.0001 15.8337" stroke="#344054" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Graph 5: Page Speed by landing pages (top 5 by spend, data from Page Speed Insights API) */}
              <div className="opportunities-section mb-4">
                <button
                  type="button"
                  className="opportunities-section-header"
                  onClick={() => toggleSection('pageSpeed')}
                  aria-expanded={openSections.pageSpeed}
                >
                  <div>
                    <h5 className="mb-1">Page Speed By Landing Page</h5>
                    {/* <p className="opportunities-section-subtitle mb-0">
                      Define audience segments and content strategy.
                    </p> */}
                  </div>
                  <span className={`opportunities-section-chevron ${openSections.pageSpeed ? 'open' : ''}`} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                {openSections.pageSpeed && (
                  <div className="opportunities-section-body">
                    {topLandingPages.length === 0 ? (
                      <p className="text-muted mb-0">No landing page data in this period.</p>
                    ) : (
                      <div className="opportunities-keywords-table border rounded overflow-hidden bg-white opp-padding">
                        <Table responsive className="mb-0">
                          <thead>
                            <tr>
                              <th className="channel_performance_header_cell">Landing Page URL</th>
                              <th className="channel_performance_header_cell">Desktop</th>
                              <th className="channel_performance_header_cell">Mobile</th>
                            </tr>
                          </thead>
                          <tbody>
                            {top5LandingPagesBySpend.map((row, i) => {
                              const ps = getPageSpeedForUrl(row.landing_page_url);
                              return (
                                <tr
                                  key={row.landing_page_url || i}
                                  className={i % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}
                                >
                                  <td className="channel_performance_table_cell">{cleanUrl(row.landing_page_url) || '--'}</td>
                                  <td className="channel_performance_table_cell">
                                    {pageSpeedLoading
                                      ? <Spinner animation="border" size="sm" className="opportunities-page-speed-spinner" />
                                      : (ps?.desktopScore != null ? String(ps.desktopScore) : '—')}
                                  </td>
                                  <td className="channel_performance_table_cell">
                                    {pageSpeedLoading
                                      ? <Spinner animation="border" size="sm" className="opportunities-page-speed-spinner" />
                                      : (ps?.mobileScore != null ? String(ps.mobileScore) : '—')}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <div className="px-3 py-2 border-top bg-light">
                          <small className="text-muted">*Top 5 landing pages by spend. Scores from Google Page Speed Insights API.</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ---- Demand Gen & Others tab ---- */}
            <div className={`admin-tab-content mt-5 ${activeTab === 'demand-gen' ? 'active' : 'hidden'}`}>
              <div className="opportunities-section mb-4">
                <div className="opportunities-overall-spending-card border rounded bg-white d-flex align-items-center justify-content-between px-4 py-3">
                  <h5 className="mb-0 opportunities-overall-spending-title">Overall Spend</h5>
                  <div className="opportunities-overall-spending-value fs-6 fw-semibold text-end">{formatCurrency(demandGenData.total_spend)}</div>
                </div>
              </div>
              <div className="opportunities-section mb-4">
                <button
                  type="button"
                  className="opportunities-section-header"
                  onClick={() => toggleSection('demandGenTargeting')}
                  aria-expanded={openSections.demandGenTargeting}
                >
                  <div>
                    <h5 className="mb-1">Targeting & Landing Pages (Non-Search campaigns)</h5>
                    {/* <p className="opportunities-section-subtitle mb-0">
                      Distribution of keywords from total targeting to active landing pages receiving meaningful traffic (&gt;10 clicks).
                    </p> */}
                  </div>
                  <span className={`opportunities-section-chevron ${openSections.demandGenTargeting ? 'open' : ''}`} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                {openSections.demandGenTargeting && (
                  <div className="opportunities-section-body">
                    <div className="border rounded overflow-hidden bg-white" style={{ padding: '16px', maxWidth: 520 }}>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={demandGenFunnelData} margin={{ top: 20, right: 16, left: 16, bottom: 24 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatFunnelValue(v)} />
                          <Bar dataKey="realValue" radius={[4, 4, 0, 0]} label={{ position: 'top', formatter: (v) => formatFunnelValue(v), fontSize: 12 }}>
                            {demandGenFunnelData.map((entry) => (
                              <Cell key={entry.name} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ---- Opportunity tab: Custom Table & Calculator (same section/table design as Search tab) ---- */}
            <div className={`admin-tab-content mt-5 ${activeTab === 'opportunity' ? 'active' : 'hidden'}`}>
              <div className="opportunities-section mb-4">
                <button
                  type="button"
                  className="opportunities-section-header"
                  onClick={() => toggleSection('opportunity')}
                  aria-expanded={openSections.opportunity}
                >
                  <div>
                    <h5 className="mb-1">Opportunities</h5>
                    {/* <p className="opportunities-section-subtitle mb-0">
                      Define audience segments and content strategy.
                    </p> */}
                  </div>
                  <span className={`opportunities-section-chevron ${openSections.opportunity ? 'open' : ''}`} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
                {openSections.opportunity && (
                  <div className="opportunities-section-body">
                    <div className="opportunities-keywords-table border rounded bg-white opp-padding">
                      <Table responsive className="mb-0">
                        <thead>
                          <tr>
                            <th className="channel_performance_header_cell">Channel</th>
                            <th className="channel_performance_header_cell">Category</th>
                            <th className="channel_performance_header_cell">Current Spend</th>
                            <th className="channel_performance_header_cell">% Efficiency</th>
                            <th className="channel_performance_header_cell">Savings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {opportunityRowsWithSavings.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}>
                              <td className="channel_performance_table_cell">{row.channel}</td>
                              <td className="channel_performance_table_cell">{row.category || '—'}</td>
                              <td className="channel_performance_table_cell">{formatCurrency(row.currentSpend)}</td>
                              <td className="channel_performance_table_cell">
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={0.5}
                                  className="form-control form-control-sm"
                                  style={{ width: '80px' }}
                                  // placeholder="e.g. 8"
                                  value={row.efficiencyPct}
                                  onChange={(e) => setOpportunityEfficiencyForRow(i, e.target.value)}
                                  aria-label={`% Efficiency for ${row.channel} ${row.category || ''}`}
                                />
                              </td>
                              <td className="channel_performance_table_cell">{formatCurrency(row.savings)}</td>
                            </tr>
                          ))}
                          <tr className="channel_performance_table_row fw-bold">
                            <td className="channel_performance_table_cell">Total</td>
                            <td className="channel_performance_table_cell">—</td>
                            <td className="channel_performance_table_cell">{formatCurrency(opportunityTotalSpend)}</td>
                            <td className="channel_performance_table_cell">—</td>
                            <td className="channel_performance_table_cell">{formatCurrency(opportunityTotalSavings)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                    {/* <div className="opportunities-monthly-saving-card border rounded bg-white p-4">
                      <p className="mb-0 opportunities-monthly-saving-value">
                        Estimated Monthly savings: {formatCurrency(opportunityTotalSavings / 12)}
                      </p>
                      {opportunityTotalSavings === 0 && (
                        <p className="mb-0 mt-2 opportunities-section-subtitle">
                          {opportunityTotalSpend === 0
                            ? 'No spend in the selected period.'
                            : ''}
                        </p>
                      )}
                    </div> */}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default Opportunities;